<template>
  <section
    :id="message._id"
    ref="message"
    :class="{'hide': removedBookmark}"
    class="message"
  >
    <header class="message__metadata message__details-wrapper">
      <div class="message__metadata-info">
        <i>
          {{ gettext('Section:') }}
          <a
            :href="getMessageSearchHrefWithNewFilters([
              { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
              { mode: '+', type: 'forum', term: `${message._source.forum}` }
            ])"
            @click="addFilters([
              { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
              { mode: '+', type: 'forum', term: `${message._source.forum}` },
            ], $event)"
          >
            <span>{{ message._source.forum }}</span>
          </a>
          <actions-menu
            :message="message"
            :siteLink="forumSiteLink"
            :filterTerm="message._source.forum"
            :filterType="'forum'"
            :noteId="message._source.forum_id"
            :noteItem="'forum'"
            :noteName="message._source.forum"
            :hasNotes="message._source.has_forum_notes"
            :indexType="'forum'"
            :showBookmark="false"
            @refresh="refresh"
          />
          {{ gettext('on site:') }}
          <a
            :href="getMessageSearchHrefWithNewFilters([
              { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
            ])"
            @click="addFilters([
              { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
            ], $event)"
          >
            <span>{{ message._source.site_name }}</span>
          </a>
          <actions-menu
            :message="message"
            :siteLink="forumSiteLink"
            :filterTerm="message._source.site_name"
            :filterType="'site_name'"
            :noteId="message._source.site_id"
            :noteItem="'site'"
            :noteName="message._source.site_name"
            :hasNotes="message._source.has_site_notes"
            :indexType="'forum'"
            @refresh="refresh"
            @bookmarkRemoved="bookmarkRemoved"
          />
        </i>
        <details class="message__details u-mt-sm">
          <summary class="message__details--forum">
            {{ gettext('Message details') }}
          </summary>
          <dl class="dl-table dl-table--dense u-mt-sm u-mb-0 u-pl-md">
            <div class="dl-table__row">
              <dt>{{ gettext('Score:') }}</dt>
              <dd>{{ message._score }}</dd>
            </div>
            <div class="dl-table__row">
              <dt>{{ gettext('Post length:') }}</dt>
              <dd>{{ message._source.message.length }}</dd>
            </div>
            <div
              v-if="message._source.forum_id"
              class="dl-table__row"
            >
              <dt>{{ gettext('Forum ID:') }}</dt>
              <dd>
                <a
                  :href="getMessageSearchHrefWithNewFilters([
                    { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
                    { mode: '+', type: 'forum_id', term: `${message._source.forum_id}` },
                  ])"
                  @click="addFilters([
                    { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
                    { mode: '+', type: 'forum_id', term: `${message._source.forum_id}` },
                  ], $event)"
                >
                  {{ message._source.forum_id }}
                </a>
              </dd>
            </div>
            <div
              v-if="message._source.topic_id"
              class="dl-table__row"
            >
              <dt>{{ gettext('Topic ID:') }}</dt>
              <dd>
                <a
                  :href="getMessageSearchHrefWithNewFilters([
                    { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
                    { mode: '+', type: 'topic_id', term: `${message._source.topic_id}` },
                  ])"
                  @click="addFilters([
                    { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
                    { mode: '+', type: 'topic_id', term: `${message._source.topic_id}` },
                  ], $event)"
                >
                  {{ message._source.topic_id }}
                </a>
              </dd>
            </div>
            <div
              v-if="message._source.post_id"
              class="dl-table__row"
            >
              <dt>{{ gettext('Post ID:') }}</dt>
              <dd>
                <a
                  :href="getMessageSearchHrefWithNewFilters([
                    { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
                    { mode: '+', type: 'post_id', term: `${message._source.post_id}` },
                  ])"
                  @click="addFilters([
                    { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
                    { mode: '+', type: 'post_id', term: `${message._source.post_id}` },
                  ], $event)"
                >
                  {{ message._source.post_id }}
                </a>
              </dd>
            </div>
            <div
              v-if="message._source.uuid"
              class="dl-table__row"
            >
              <dt>{{ gettext('Doc ID:') }}</dt>
              <dd>
                {{ message._source.uuid }}
              </dd>
            </div>
            <div
              v-if="message._source.url"
              class="dl-table__row"
            >
              <dt>{{ gettext('URL:') }}</dt>
              <dd>
                <copy-to-clipboard
                  :textToClipboard="message._source.url"
                />
              </dd>
            </div>
            <div
              v-if="message._source.userinfo"
              class="dl-table__row"
            >
              <dt>{{ gettext('User Info:') }}</dt>
              <dd>
                {{ message._source.userinfo }}
              </dd>
            </div>
          </dl>
        </details>
      </div>
      <div
        v-if="hasProcessMessagesPerms"
        class="message__update_processed_date"
        title="A message is processed after it's media been sent to Arachnid."
      >
        Processed
        <input
          :id="'process-' + message._id"
          type="checkbox"
          :checked="message._source.processed_date != null"
          @change="updateMessageProcessed(message)"
        >
        <label :for="'process-' + message._id" />
      </div>
      <div
        v-if="siteSourceCheck"
        class="message__metadata-onion"
      >
        <img
          title="This is a forum found on the Tor network"
          width="50"
          src="/static/images/logos/onion.png"
        >
      </div>
    </header>
    <div class="message__header message__header--forum">
      <p class="h4 u-mb-xs">
        <a
          :href="getMessageSearchHrefWithNewFilters([
            { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
            { mode: '+', type: 'topic_id', term: `${message._source.topic_id}` },
          ])"
          @click="addFilters([
            { mode: '+', type: 'site_name', term: `${message._source.site_name}` },
            { mode: '+', type: 'topic_id', term: `${message._source.topic_id}` },
          ], $event)"
        >
          <span>
            <template v-if="message._source.topic">{{ message._source.topic }}</template>
            <i v-else>{{ pgettext('no post topic available', '(no topic name)') }}</i>
          </span>
        </a>
        <actions-menu
          :message="message"
          :siteLink="topicLink"
          :filterType="'topic_id'"
          :filterTerm="message._source.topic_id"
          :noteId="message._source.topic_id"
          :noteName="message._source.topic"
          :noteItem="'Topic'"
          :hasNotes="message._source.has_topic_notes"
          :indexType="'forum'"
          :showBookmark="false"
          @refresh="refresh"
          @bookmarkRemoved="bookmarkRemoved"
        />
      </p>
      <p class="u-m-0">
        {{ pgettext('noting author of post', 'by') }}
        <a
          v-if="message._source.username"
          :href="getMessageSearchHrefWithNewFilters([
            { mode: '+', type: 'username', term: `${message._source.username}` },
          ])"
          @click="addFilters([
            { mode: '+', type: 'username', term: `${message._source.username}` },
          ], $event)"
        >
          <span><b>{{ message._source.username }}</b></span>
        </a>
        <i v-else>{{ pgettext('(unknown)') }}</i>
        <actions-menu
          :message="message"
          :siteLink="userLink"
          :filterType="'username'"
          :filterTerm="message._source.username"
          :noteId="message._source.site_id"
          :noteName="message._source.username"
          :noteItem="'user'"
          :hasNotes="message._source.has_user_notes"
          :indexType="'forum'"
          :showBookmark="false"
          @refresh="refresh"
          @bookmarkRemoved="bookmarkRemoved"
        />
        {{ pgettext('noting date of post', 'on') }}
        <time datetime="">
          {{ readableDate(message._source.posted_date) }}
        </time>
        <a
          class="u-pl-sm"
          :href="`/en/user/detail/${message._source.username}/${message._source.site_name}/`"
        >
          View Username Details
          <svg
            class="icon u-fill-current u-fontsize-sm"
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 100 100"
          >
            <path d="M47 2L35 14l27 27-40-1H6v20h16l40-1-27 27 12 12 48-48z" />
          </svg>
        </a>
      </p>
    </div>
    <message-result-body
      messageType="forum"
      :message="message"
      :indexType="'forum'"
      :siteLink="postLink"
      @toggle-post="keepMessageInView"
    />
    <message-result-details
      v-if="message._source.has_media !== false"
      :docId="message._id"
      :mediaSearchUrl="mediaSearchUrl"
      :messageSource="message"
      :indexType="'forum'"
    />
  </section>
</template>
<script>
import MessageResultBody from './MessageResultBody'
import MessageResultDetails from './MessageResultDetails'
import ActionsMenu from '../ActionsMenu'
import CopyToClipboard from '../CopyToClipboard'
import axios from 'axios'
import {getMessageSearchHrefWithNewFilters} from '../utils'
import Cookies from 'js-cookie'

export default {
  name: 'MessageResultForum',
  components: {
    MessageResultBody,
    MessageResultDetails,
    ActionsMenu,
    CopyToClipboard,
  },
  props: {
    message: {
      type: Object,
      required: true
    },
    mediaSearchUrl: {
      type: String,
      default: ''
    },

  },
  data() {
    return {
      note: '',
      noteType: '',
      noteCategory: '',
      forumSiteLink: null,
      forumLink: null,
      topicLink: null,
      userLink: null,
      postLink: null,
      removedBookmark: false,
      hasProcessMessagesPerms: false,
    }
  },

  computed: {
    siteSourceCheck() {
      if (this.message._source.url) {
        if (this.message._source.url.includes('.onion')) {
          return true
        }
      } else if (this.message._source.site) {
        if (this.message._source.site.includes('.onion')) {
          return true
        }
      }
      return false
    },
  },

  mounted() {
    this.forumSiteLink = new URL('/en/message-search/forum-sites/' + this.message._source.site_name + '/', document.baseURI).href

    this.forumLink = new URL('/en/message-search/forum-sites/' + this.message._source.site_name + '/' +
        this.message._source.forum + '/', document.baseURI).href

    this.topicLink = new URL(this.getTopicLink(this.message._source), document.baseURI).href

    this.userLink = new URL('/en/message-search/?filters=site_name:' + this.message._source.site_name + ',username:' +
        this.message._source.username, document.baseURI).href

    this.postLink = new URL('/en/message-search/?filters=site_name:' + this.message._source.site_name + ',post_id:' +
        this.message._source.post_id, document.baseURI).href

    this.hasProcessMessagesPerms = window.process_messages_perm
  },

  methods: {
    encodeURL(topic) {
      return encodeURIComponent(topic)
    },
    getTopicLink(topic) {
      let siteName = (topic.site_name) ? 'site_name:' + topic.site_name : ''
      let topicName = (topic.topic) ? ',topic:' + this.encodeURL(topic.topic) : ''
      let topicId = (topic.topic_id) ? ',topic_id:' + topic.topic_id : ''
      return '/en/message-search/?filters=' + siteName + topicName + topicId
    },
    readableDate(timestamp) {
      let date = new Date(timestamp * 1000)
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
    getMessageSearchHrefWithNewFilters(filters) {
      return getMessageSearchHrefWithNewFilters(filters)
    },
    addFilters(filters, event) {
      if (event && !event.ctrlKey) {
        event.preventDefault()
        this.$store.commit('addFilters', filters)
      }
    },

    refresh() {
      this.$emit('refreshed')
    },

    keepMessageInView(isShowing) {
      let messageBounds = this.$el.getBoundingClientRect()

      if (isShowing && messageBounds.top < 0) {
        let y = (window.pageYOffset + messageBounds.top) - 10
        let x = 0

        // Keep the message in view on the page when collapsing it:
        window.scrollTo({
          top: y,
          left: x,
          behavior: 'smooth'
        })
      }
    },
    bookmarkRemoved() {
      let filters = this.$store.state.filters
      if (filters.length > 0) {
        filters.forEach(filter => {
          if (filter.term === 'chat' || filter.term === 'forum') {
            this.removedBookmark = true
            this.$refs.message.addEventListener('animationend', () => {
              this.$emit('bookmarkRemoved')
            })
          }
        })
      }
    },
    updateMessageProcessed(message) {
      const csrftoken = Cookies.get('csrftoken')
      const hasProcessedDate = message._source.processed_date != null
      const requestParams = {
        'doc_id': message._id,
        'current_processed_date': hasProcessedDate ? message._source.processed_date : '',
        'index': message._index
      }
      axios.post(
        '/en/message-search/processed_state/update/',
        requestParams,
        {'headers': {'X-CSRFToken': csrftoken}},
      ).then(response => {
        if (response.status === 200) {
          // request was a success so update the checkmark state
          if (message._source.processed_date != null) {
            message._source.processed_date = null
          } else {
            const today = new Date()
            message._source.processed_date = today.getUTCDate()
            // hide the message
            const params = new URLSearchParams(window.location.search)
            const urlFilters = params.get('filters')
            if (urlFilters?.includes('processed_date')) {
              document.getElementById(message._id)?.classList.add('hidden')
            }
          }
        }
      }).catch(e => {
        console.error(e) // nothing special to do with error
      })
    }
  },
}
</script>

<style scoped>
.hide {
  animation-name: fadeOutBookmark;
  animation-duration: 1s;
  animation-delay: 0.5s;
}

@keyframes fadeOutBookmark {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
.message__update_processed_date {
  grid-column: 2;
  padding-top: 0.25em;
  padding-left: 0.5em;
  text-align: center;
}
.message__update_processed_date > label {
  left: 50%;
  margin-left: -10px;
  height: 50px;
}
</style>
